import React, { useEffect, useRef, useState } from "react";
// Make sure all imports are correct and necessary
import { NetworkRequest } from "../../../common/models";
import { Callout, CalloutVariant } from "../../components/Callout";
import {
  DisclaimerModal,
  EditPrivacyCenter,
  useEditPrivacyCenter,
} from "../../components/privacyCenter/EditPrivacyCenter";
import { Breadcrumbs, Button } from "@truevault/aquilo";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { PrivacyCenterTabsNew } from "./privacy-center/PrivacyCenterTabsNew";
import { ProgressFooter, ProgressFooterActions } from "../get-compliant/ProgressFooter";
import { useHistory } from "react-router";
import { Routes } from "../../root/routes";
import { PrintPreviewPublishButtons } from "./privacy-center/us-state-notices/USStateNoticesPage";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "./PrivacyNoticeChecklistItems";
import { didFinishPoliciesAndNoticesOnboarding } from "../../../common/hooks/useOnboardingProgress";

export const DevInstructionsStartWithTruevaultPrivacyPolicyTemplatePromptSlug =
  "answered-start-with-truevault-privacy-policy-template";

export const usePrivacyCenterPreviewPopup = (path?: string) => {
  const [previewWindowRef, setPreviewWindowRef] = useState<Window | null>(null);
  const openPreviewNotice = () => {
    let previewWindowUrl = Routes.privacyCenter.previewPopup;
    if (path) {
      previewWindowUrl += `?path=${encodeURIComponent(path)}`;
    }
    if (previewWindowRef && !previewWindowRef.closed) {
      previewWindowRef.location.href = previewWindowUrl;
    } else {
      setPreviewWindowRef(
        window.open(previewWindowUrl, "_blank", "toolbar=0,location=0,width=1400,height=900"),
      );
    }
  };
  return openPreviewNotice;
};

export const PrivacyCenter = () => {
  const editPrivacyCenter = useEditPrivacyCenter();
  const org = editPrivacyCenter?.props?.org;

  const history = useHistory();

  const [everRendered, setEverRendered] = useState(false);

  const openPreviewNotice = usePrivacyCenterPreviewPopup();

  // Use a ref to track whether the Publish button is enabeld or disabled so it can be updated by the <EditPrivacyCenter> child component
  const publishButtonEnabledRef = useRef(false);
  const [showPublishDateModal, setShowPublishDateModal] = useState(false);

  const { answers: developerInstructionsAnswers } = useSurvey(
    org?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  // ever-loaded effect
  useEffect(() => {
    if (
      NetworkRequest.areFinished(
        editPrivacyCenter.requests.privacyCenter,
        editPrivacyCenter.requests.notice,
        editPrivacyCenter.requests.collectionGroups,
        editPrivacyCenter.requests.allNoticesRequest,
      )
    ) {
      setEverRendered(true);

      // Scroll to the top of the page after loading
      // This is to workaround where some section editor will intermittently steal focus on load, causing the page to scroll to that section
      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        window.scrollTo(0, 0);
      }, 750);
    }
  }, [
    editPrivacyCenter.requests.privacyCenter,
    editPrivacyCenter.requests.notice,
    editPrivacyCenter.requests.collectionGroups,
    editPrivacyCenter.requests.allNoticesRequest,
  ]);

  if (!everRendered) {
    return <LoadingPageWrapper />;
  }

  const privacyCenter = editPrivacyCenter?.props?.privacyCenter;

  const didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt =
    developerInstructionsAnswers &&
    developerInstructionsAnswers[
      DevInstructionsStartWithTruevaultPrivacyPolicyTemplatePromptSlug
    ] === "true";

  // Only enable the preview button when: (1) the user has chosen to either start from scratch or from a TrueVault template (2) website logo is uploaded and (3) website URL is entered
  const isPreviewEnabled =
    privacyCenter?.logoUrl?.length > 0 &&
    privacyCenter?.logoLinkUrl?.length > 0 &&
    didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt;

  // For now, the step completion condition is the same as the preview condition
  const isStepComplete = isPreviewEnabled;

  const showFooter = !didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers);

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        showFooter && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextDisabled={!isStepComplete}
                nextDisabledTooltip={
                  didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt
                    ? "Set a website logo and URL to continue"
                    : "Make a selection for your initial privacy policy"
                }
                nextContent="Done with Privacy Policy"
                nextOnClick={() => {
                  if (org?.featureGdpr || org?.featurePipeda) {
                    history.push(Routes.privacyCenter.internationalNotices);
                  } else if (!privacyCenter?.hideCcpaSection) {
                    history.push(Routes.privacyCenter.usStates);
                  } else {
                    history.push(Routes.privacyCenter.theme);
                  }
                }}
              />
            }
          />
        )
      }
    >
      <div>
        <div className="tw-boundary sticky-header">
          <Breadcrumbs title="Policies & Notices" sourceRoute={Routes.privacyCenter.root} />

          <PrivacyCenterTabsNew
            org={org}
            selectedTabKey="privacy-policy"
            enableNextTab={isStepComplete}
            enableAllTabs={didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)}
            trailingContent={
              <PrintPreviewPublishButtons
                orgId={org?.id}
                privacyCenter={editPrivacyCenter?.props?.privacyCenter}
                draftNoticePublicId={editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId}
                hasPublishedNotice={
                  editPrivacyCenter?.props?.latestPublishedNoticeUpdatedOn != null
                }
                onPreviewClick={openPreviewNotice}
                onPublishClick={() => setShowPublishDateModal(true)}
                showPdfPreviewButton={didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt}
                // In the onboarding/FTUE flow, we only enable the Publish button on the last step of the flow, so it should be disabled on this page if we're in FTUE
                isPublishEnabled={
                  publishButtonEnabledRef.current &&
                  didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                }
                isPreviewEnabled={isPreviewEnabled}
                previewDisabledTooltip={
                  didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt
                    ? "Set a website logo and URL to preview your privacy policy."
                    : "Make a selection for your initial privacy policy."
                }
                publishDisabledTooltip={
                  !didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                    ? "Complete the Themes step to publish your Privacy Center."
                    : undefined
                }
              />
            }
          />
        </div>

        <div className="privacy-center-settings privacy-center" style={{ marginTop: "0px" }}>
          {editPrivacyCenter.props.hasSaved && (
            <Callout variant={CalloutVariant.Yellow} className="mb-mdlg">
              Your changes have been saved and will take a few minutes to appear in your Privacy
              Policy.
            </Callout>
          )}
          <EditPrivacyCenter
            {...editPrivacyCenter.props}
            publishButtonEnabledRef={publishButtonEnabledRef}
            showPublishDateModal={showPublishDateModal}
            setShowPublishDateModal={setShowPublishDateModal}
          />
        </div>
      </div>
    </StandardPageWrapper>
  );
};

export function StartWithTruevaultPrivacyPolicyTemplatePrompt({
  onClick,
}: {
  onClick: (action: "START_WITH_TEMPLATE" | "START_FROM_SCRATCH") => void;
}) {
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  return (
    <>
      <div className="tw-boundary" style={{ marginBottom: "48px" }}>
        <div className="font-black text-neutral-800 text-[18px] mb-6">
          Build or add your Privacy Policy
        </div>
        <div className="bg-neutral-0 border border-solid border-black rounded-lg flex justify-center py-8 max-w-[1000px]">
          <div className="flex flex-col items-center gap-5">
            <div>
              <img
                src="/assets/images/stay_compliant/privacy_center/build-or-add-privacy-policy-prompt-illustration.svg"
                className="w-[532px] h-[300px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button color="tertiary" onClick={() => setShowDisclaimerModal(true)}>
                Start With TrueVault Template
              </Button>
              <Button color="tertiary" onClick={() => onClick("START_FROM_SCRATCH")}>
                Copy Paste Your Own
              </Button>
            </div>
          </div>
        </div>
      </div>
      {showDisclaimerModal && (
        <DisclaimerModal
          onAcceptDisclosure={() => {
            onClick("START_WITH_TEMPLATE");
          }}
          onClose={() => setShowDisclaimerModal(false)}
        />
      )}
    </>
  );
}
